import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SocialPostMedia } from 'fym-shared/src/music24/socialPostFormat';
import { TypedMemo } from '../../../../common/types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { captionLength, platformMeta } from '../../platformMeta';
import { xMessages } from '../../xMessages';
import { ChannelXPosts } from '../../channels/ChannelXPosts';
import { ScheduledPostFormat, SerializedSocialChannel } from '../../types';
import { composerFormatLine } from '../composerFormatLine';
import { composerFormatCannot, composerFormatLabels } from '../composerFormatMessages';
import { SettingsAccountRow } from '../settings/SettingsAccountRow';
import { SettingsCard } from '../settings/SettingsCard';
import { accountSectionMessages } from './accountSectionMessages';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the settings sections; each part uses its own slice of it */
import styles from '../settings/PlatformSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type AccountSectionPlatform = 'instagram' | 'facebook' | 'threads' | 'x';

interface ComposerAccountSectionProps {
  platform: AccountSectionPlatform;
  channel: SerializedSocialChannel;
  caption: string;
  media: SocialPostMedia | null;
  format: ScheduledPostFormat;
}

const TITLES = {
  instagram: accountSectionMessages.instagramTitle,
  facebook: accountSectionMessages.facebookTitle,
  threads: accountSectionMessages.threadsTitle,
  x: xMessages.accountTitle,
} as const;

/**
 * The account section of Instagram, Facebook Pages, Threads and X: the account (a Facebook Page
 * shows its name and picture) that gets the post, the format that the platform makes of it, the
 * Threads and X character limits, the X posts of this month and one line that says Music24 posts
 * on the user's behalf (design x-connector c3 for X).
 */
const ComposerAccountSectionComponent: React.FC<ComposerAccountSectionProps> = ({
  platform,
  channel,
  caption,
  media,
  format,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const meta = platformMeta[platform];
  const name = channel.connection?.displayName ?? channel.handle ?? meta.name;
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const line = useMemo(() => composerFormatLine(platform, media, format), [format, media, platform]);
  const over = platform === 'threads' ? caption.length - meta.captionLimit : 0;
  // X counts each link as 23 characters.
  const xOver = platform === 'x' ? captionLength('x', caption) - meta.captionLimit : 0;

  return (
    <SettingsCard title={intl.formatMessage(TITLES[platform])} testId={`${platform}-settings`}>
      <SettingsAccountRow
        eyebrow={intl.formatMessage(
          platform === 'facebook' ? accountSectionMessages.postingToPage : accountSectionMessages.postingToAccount
        )}
        name={name}
        handle={channel.handle}
        avatarUrl={channel.connection?.avatarUrl ?? null}
      />
      {platform === 'x' ? (
        <p className={styles.small} data-testid="x-limit">
          {xOver > 0
            ? intl.formatMessage(xMessages.overLimit, { count: xOver })
            : intl.formatMessage(xMessages.linkNote)}
        </p>
      ) : null}
      {platform === 'x' && channel.xPosts !== undefined && channel.xPosts !== null ? (
        <ChannelXPosts usage={channel.xPosts} />
      ) : null}
      {platform === 'x' ? null : platform === 'threads' ? (
        <p className={styles.small} data-testid="threads-limit">
          {over > 0
            ? intl.formatMessage(accountSectionMessages.threadsOverLimit, { over })
            : intl.formatMessage(accountSectionMessages.threadsLimit, {
                limit: meta.captionLimit,
                count: caption.length,
              })}
        </p>
      ) : (
        <p className={styles.small} data-testid={`${platform}-format`}>
          <b>
            {intl.formatMessage(line.format === null ? composerFormatCannot.label : composerFormatLabels[line.format])}
          </b>
        </p>
      )}
      <p className={styles.consent} data-testid={`${platform}-on-behalf`}>
        {withBrandWord(intl.formatMessage(accountSectionMessages.onBehalf, { account: name }))}{' '}
        <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={openChannels}>
          {intl.formatMessage(accountSectionMessages.change)}
        </button>
      </p>
    </SettingsCard>
  );
};

export const ComposerAccountSection = TypedMemo(ComposerAccountSectionComponent);
